import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LicenseBlockedScreen from "@/pages/LicenseBlockedScreen";
import { ShieldAlert, X } from "lucide-react";

/**
 * LicenseGate — wrapper que renderiza LicenseBlockedScreen quando a empresa ativa
 * do usuário está com a licença bloqueada (inativa ou expirada).
 *
 * Estratégia:
 *  - Prefere `currentEmpresa.license_status` (já vem no /empresas quando o usuário loga)
 *  - Fallback: consulta /license/status (usado para entregadores mobile que não têm
 *    listagem de empresas carregada)
 *  - Super Admin bypassa (backend também bypassa) MAS recebe um banner vermelho
 *    "SA VIEW — Licença Bloqueada" no topo pra saber que o cliente vê tela de bloqueio.
 *
 * Props: children (conteúdo protegido), variant ("web"|"mobile")
 */
export function LicenseGate({ children, variant = "web" }) {
  const { user, currentEmpresa, currentEmpresaId, loading } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saHintDismissed, setSaHintDismissed] = useState(false);

  const isSA = !user?._impersonating && (user?.role === "super_admin"
    || (user?.role === "admin" && !(user?.empresa_ids || []).length));

  const fetchStatus = useCallback(async () => {
    if (!user || isSA) return;
    setChecking(true);
    try {
      const r = await api.get("/license/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    } finally {
      setChecking(false);
    }
  }, [user, isSA]);

  useEffect(() => {
    if (loading || !user || isSA) { setStatus(null); return; }
    if (currentEmpresa?.license_status) {
      setStatus({
        ...currentEmpresa.license_status,
        empresa_nome: currentEmpresa.nome,
        empresa_id: currentEmpresa.id,
      });
    } else {
      fetchStatus();
    }
  }, [loading, user, isSA, currentEmpresa, currentEmpresaId, fetchStatus]);

  // Reseta o dismiss quando muda de empresa
  useEffect(() => { setSaHintDismissed(false); }, [currentEmpresaId]);

  useEffect(() => {
    const onBlocked = (e) => {
      if (!e?.detail) return;
      setStatus((prev) => ({
        blocked: true,
        reason: e.detail.reason,
        empresa_id: e.detail.empresa_id,
        empresa_nome: e.detail.empresa_nome,
        expires_at: e.detail.expires_at,
        observacao: e.detail.observacao,
        support: prev?.support,
      }));
    };
    window.addEventListener("license-blocked", onBlocked);
    return () => window.removeEventListener("license-blocked", onBlocked);
  }, []);

  if (loading || checking) return children;
  if (!user) return children;

  // Super Admin: bypassa o bloqueio mas mostra banner de aviso pra saber que o
  // cliente daquela empresa está vendo tela de bloqueio.
  if (isSA) {
    const cs = currentEmpresa?.license_status;
    if (cs?.blocked && !saHintDismissed) {
      return (
        <>
          <SABlockedHint
            reason={cs.reason}
            empresaNome={currentEmpresa?.nome}
            expiresAt={cs.expires_at}
            onDismiss={() => setSaHintDismissed(true)}
          />
          {children}
        </>
      );
    }
    return children;
  }

  if (status?.blocked) return <LicenseBlockedScreen status={status} variant={variant} />;
  return children;
}

// Banner vermelho — Super Admin SEM ser barrado, mas ciente de que a empresa está bloqueada.
function SABlockedHint({ reason, empresaNome, expiresAt, onDismiss }) {
  const label = reason === "expired"
    ? `Licença EXPIROU${expiresAt ? ` em ${fmtDate(expiresAt)}` : ""}`
    : "Licença INATIVA";
  return (
    <div
      className="bg-rose-600 text-white px-4 py-2.5 flex items-center gap-3 text-sm font-medium"
      data-testid="sa-license-blocked-hint"
    >
      <ShieldAlert className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-extrabold uppercase tracking-wide">Super Admin View</span>
        <span className="ml-2">
          Você bypassou o bloqueio, mas todos os usuários de <b>{empresaNome || "esta empresa"}</b> estão vendo tela de bloqueio ({label}).
        </span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="p-1 rounded hover:bg-white/20 shrink-0"
        title="Fechar aviso"
        aria-label="Fechar aviso"
        data-testid="sa-license-blocked-hint-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
